import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components, operations } from '../tipos/entregas';

/**
 * Operaciones de Entregas y Notificaciones que usan «Avisos» (5.1, 5.2) y «Mensajes» (5.3 a 5.5), con su `operationId` y
 * los tipos generados de `entregas.yaml`. Las de la campana (`listMyNotices`, `getMyNoticeSummary`, marcar leído, marcar
 * todos y archivar) están en `operaciones.ts`; `listDeliveries` y `resendDelivery`, en `ops/clientes.ts` (la pestaña
 * «Entregas» de la ficha); `listRoles`, en `ops/equipo.ts`.
 *
 * - Las reglas de «Quién recibe avisos», los recordatorios y las plantillas personalizadas llevan su `version` en el
 *   cuerpo (`0` mientras el negocio no las ha cambiado): se cambian con `If-Match` con esa versión (`versionEntregas`).
 * - Lo que crea algo o avisa a alguien (personalizar una plantilla, reenviar) lleva `Idempotency-Key`.
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Gravedad = Esquema<'NoticeSeverity'>;
export type EstadoAviso = Esquema<'NoticeReceiptState'>;
export type MiTipoAviso = Esquema<'MyNoticeType'>;
export type SilencioAviso = Esquema<'NoticeMute'>;
export type TipoAviso = Esquema<'NoticeType'>;
export type RolAviso = Esquema<'RoleRef'>;
export type CanalEntrega = Esquema<'DeliveryChannel'>;
export type EstadoEntrega = Esquema<'DeliveryStatus'>;
export type Entrega = Esquema<'Delivery'>;
export type FichaEntrega = Esquema<'DeliveryDetail'>;
export type Intento = Esquema<'DeliveryAttempt'>;
export type ResultadoIntento = Esquema<'AttemptResult'>;
export type MotivoIntento = Esquema<'AttemptReason'>;
export type Plantilla = Esquema<'DeliveryTemplateSummary'>;
export type ListaPlantillas = Esquema<'DeliveryTemplateList'>;
export type FichaPlantilla = Esquema<'DeliveryTemplateDetail'>;
export type VersionPlantilla = Esquema<'DeliveryTemplateVersion'>;
export type Hueco = Esquema<'EditableSlot'>;
export type VistaPrevia = Esquema<'DeliveryTemplatePreview'>;
export type PeticionVistaPrevia = Esquema<'DeliveryTemplatePreviewRequest'>;
export type EstadoMeta = Esquema<'TemplateProviderStatus'>;
export type Recordatorio = Esquema<'ReminderSetting'>;
export type ListaRecordatorios = Esquema<'ReminderSettingList'>;
export type CambioRecordatorio = Esquema<'ReminderSettingWrite'>;
export type MiEntrega = Esquema<'MyDelivery'>;
export type ListaMisEntregas = Esquema<'MyDeliveryList'>;
export type AjusteCanales = Esquema<'DeliveryChannelSettings'>;
export type CambioAjusteCanales = Esquema<'DeliveryChannelSettingsWrite'>;
export type ConsultaPlantillas = NonNullable<operations['listDeliveryTemplates']['parameters']['query']>;
type Lista<T> = { data: T[]; next_cursor?: string | null };

/** `If-Match` de lo que trae su versión en el cuerpo (reglas, recordatorios y plantillas; `0` sin guardar nunca). */
export function versionEntregas(version: number): string {
    return String(version);
}

const r = (businessId: string, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = encodeURIComponent;

export function operacionesEntregas(api: ClienteApi) {
    return {
        // ---- 5.1 Tus avisos ----
        /** listMyNoticeTypes (G297): los tipos que me llegan, con su nombre, si se pueden silenciar y si los silencié. */
        listMyNoticeTypes: (businessId: string) => api.pedir<Lista<MiTipoAviso>>('GET', r(businessId, '/my-notice-types')),
        /** listMyNoticeMutes: los tipos silenciados (solo el código y cuándo). */
        listMyNoticeMutes: (businessId: string) => api.pedir<Lista<SilencioAviso>>('GET', r(businessId, '/notice-mutes')),
        /** muteNoticeType: 409 en los críticos (no se silencian). */
        muteNoticeType: (businessId: string, tipo: string) => api.pedir<SilencioAviso>('PUT', r(businessId, `/notice-mutes/${id(tipo)}`)),
        /** unmuteNoticeType. */
        unmuteNoticeType: (businessId: string, tipo: string) => api.pedir<undefined>('DELETE', r(businessId, `/notice-mutes/${id(tipo)}`)),

        // ---- 5.2 Quién recibe avisos ----
        /** listNoticeTypes (`entregas.avisos.configurar`): los tipos repartidos por roles, con su versión. */
        listNoticeTypes: (businessId: string) => api.pedir<Lista<TipoAviso>>('GET', r(businessId, '/notice-types')),
        /** updateNoticeTypeRecipients: los roles de un tipo (al menos uno; un crítico siempre con `owner`). */
        updateNoticeTypeRecipients: (businessId: string, tipo: string, roles: readonly RolAviso[], version: number) =>
            api.pedir<TipoAviso>('PUT', r(businessId, `/notice-types/${id(tipo)}/recipients`), { cuerpo: { roles }, siCoincide: versionEntregas(version) }),
        /** updateRoleNoticeTypes (G300): «Activar todos» y «Copiar de otro rol», todo o nada con `base_versions`. */
        updateRoleNoticeTypes: (businessId: string, rol: RolAviso, tipos: readonly string[], versiones: Readonly<Record<string, number>>) =>
            api.pedir<Lista<TipoAviso>>('PUT', r(businessId, `/roles/${id(rol)}/notice-types`), { cuerpo: { notice_types: tipos, base_versions: versiones } }),

        // ---- 5.3 Mensajes a clientes ----
        /** listDeliveryTemplates (`entregas.plantillas.gestionar`): una fila por mensaje, canal e idioma. */
        listDeliveryTemplates: (businessId: string, filtros: ConsultaPlantillas & Pagina = {}) =>
            api.pedir<ListaPlantillas>('GET', r(businessId, `/delivery-templates${consulta({ ...filtros })}`)),
        /** getDeliveryTemplate: la personalización con sus versiones y su `version`. */
        getDeliveryTemplate: (businessId: string, plantillaId: string): Promise<ConVersion<FichaPlantilla>> =>
            api.pedirConVersion<FichaPlantilla>('GET', r(businessId, `/delivery-templates/${id(plantillaId)}`)),
        /** previewDeliveryTemplate: el texto con datos de ejemplo (y los avisos, p. ej. «necesita aprobación de Meta»). */
        previewDeliveryTemplate: (businessId: string, peticion: PeticionVistaPrevia) =>
            api.pedir<VistaPrevia>('POST', r(businessId, '/delivery-templates/preview'), { cuerpo: peticion }),
        /** customizeDeliveryTemplate: la primera vez (409 si ya existe: entonces, versión nueva). */
        customizeDeliveryTemplate: (businessId: string, datos: Esquema<'DeliveryTemplateCreate'>, clave = claveIdempotencia()) =>
            api.pedir<FichaPlantilla>('POST', r(businessId, '/delivery-templates'), { cuerpo: datos, idempotencia: clave }),
        /** createDeliveryTemplateVersion: versión nueva de los huecos, con la versión leída. */
        createDeliveryTemplateVersion: (businessId: string, plantillaId: string, valores: Readonly<Record<string, string>>, version: string) =>
            api.pedir<VersionPlantilla>('POST', r(businessId, `/delivery-templates/${id(plantillaId)}/versions`), { cuerpo: { slot_values: valores }, siCoincide: version }),
        /** resetDeliveryTemplate: vuelve al texto de Reservaria (las versiones quedan de historial). */
        resetDeliveryTemplate: (businessId: string, plantillaId: string, version: string) =>
            api.pedir<FichaPlantilla>('POST', r(businessId, `/delivery-templates/${id(plantillaId)}/reset`), { siCoincide: version }),

        // ---- 5.4 Recordatorios ----
        /** listReminderSettings (`entregas.recordatorios.configurar`): con `entitlement_active` del bloque. */
        listReminderSettings: (businessId: string) => api.pedir<ListaRecordatorios>('GET', r(businessId, '/reminder-settings')),
        /** updateReminderSetting: con `If-Match` de su versión (`0` la primera vez). */
        updateReminderSetting: (businessId: string, tipo: string, cambio: CambioRecordatorio, version: number) =>
            api.pedir<Recordatorio>('PUT', r(businessId, `/reminder-settings/${id(tipo)}`), { cuerpo: cambio, siCoincide: versionEntregas(version) }),

        // ---- 5.8 Canales de envío (G562) ----
        /** getDeliveryChannelSettings (`entregas.canales.configurar`): «Enviar por WhatsApp», el aviso de consumo y el saldo. */
        getDeliveryChannelSettings: (businessId: string) => api.pedir<AjusteCanales>('GET', r(businessId, '/delivery-channel-settings')),
        /** updateDeliveryChannelSettings: con `If-Match` de su versión (`0` la primera vez); encender pide `whatsapp_consumption_acknowledged`. */
        updateDeliveryChannelSettings: (businessId: string, cambio: CambioAjusteCanales, version: number) =>
            api.pedir<AjusteCanales>('PUT', r(businessId, '/delivery-channel-settings'), { cuerpo: cambio, siCoincide: versionEntregas(version) }),

        // ---- 5.5 Enviados ----
        /** getDelivery (`entregas.registro.ver`): lo que llevaba y cada intento, con el destino enmascarado. */
        getDelivery: (businessId: string, entregaId: string) => api.pedir<FichaEntrega>('GET', r(businessId, `/deliveries/${id(entregaId)}`)),
    };
}
